import type React from 'react';
import type { RefObject } from 'react';

import classNames from 'classnames';

import type {
  MetadataValue,
  MetadataValues,
} from '@iconik-io/iconik-api/src/schema_patched/metadata';
import type { MetadataMetadataFieldSchema } from '@iconik-io/iconik-api/src/services/metadata';
import type { MultiSelectFilterValue } from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { useAppSelector } from '../../store/react_redux_typed';
import type { FCC } from '../../types/common';
import Alert from '../../ui/alert/alert';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import type { MetadataFieldRenderer } from '../metadata_form_field/metadata_form_field';
import type { MetadataFieldSearchRenderer } from '../metadata_form_field/search';
import MetadataFields from './metadata_fields';
import MetadataForm from './metadata_form';

import styles from './metadata_form_container.module.css';

type Props = {
  className?: string;
  componentClass?: string;
  disabled?: boolean;
  initialValuesSetter?: <V>(value: V) => boolean;
  loading: boolean;
  metadataViewId?: string;
  fieldRenderer?: MetadataFieldRenderer | MetadataFieldSearchRenderer;
  metadataValues:
    | MetadataValues
    | Record<string, MetadataValue | MultiSelectFilterValue>;
  onFormChange?: (data: MetadataValues) => void;
  onFormSubmit?: (data: MetadataValues) => void;
  onLayoutChanged?: () => void;
  prefix?: string;
  vertical: boolean;
  displayFieldsIfNoView?: boolean;
  objectType?: string;
  onModifierValueChange?: (key: string, value: unknown) => void;
  onFormClick?: (event: React.MouseEvent<HTMLElement>) => void;
  formRef?: RefObject<HTMLFormElement | null>;
  onInvalidSubmit?: () => void;
  updateOnSideEffect?: boolean;
  isSaveDisabled?: boolean;
};

const MetadataFormContainer: FCC<Props> = (props) => {
  const {
    className,
    fieldRenderer,
    onFormChange,
    onFormSubmit,
    metadataViewId,
    initialValuesSetter,
    children,
    metadataValues,
    componentClass,
    disabled,
    prefix,
    loading,
    vertical,
    displayFieldsIfNoView,
    onLayoutChanged,
    objectType,
    onModifierValueChange,
    onFormClick,
    formRef,
    onInvalidSubmit,
    updateOnSideEffect,
    isSaveDisabled,
  } = props;
  const metadataFields = useAppSelector(
    (state) => state.entities.metadata_fields,
  );
  const metadataViews = useAppSelector(
    (state) => state.entities.metadata_views,
  );
  if (loading) {
    return <LoadingBar className={styles.loading_bar} />;
  }
  if (!metadataViewId && !displayFieldsIfNoView) {
    return null;
  }
  let metadataView = metadataViewId ? metadataViews[metadataViewId] : undefined;

  if (
    !metadataViewId &&
    displayFieldsIfNoView &&
    Object.keys(metadataValues).length
  ) {
    const valueFields: MetadataMetadataFieldSchema[] = [];
    Object.keys(metadataValues).forEach((key) => {
      const name = prefix ? key.replace(prefix, '') : key;
      if (metadataFields[name]) {
        valueFields.push(metadataFields[name]);
      }
    });

    metadataView = {
      id: '0',
      name: 'unknown',
      view_fields: valueFields,
    };
  }
  if (!metadataView) {
    return null;
  }

  return (
    <div
      className={classNames(styles.wrapper, className)}
      onClick={onFormClick}
      aria-label="Metadata Form Container"
    >
      {displayFieldsIfNoView &&
      Object.keys(metadataFields).length &&
      Object.keys(metadataValues).length > metadataView.view_fields.length ? (
        <Alert variant="warning">Some fields are not available</Alert>
      ) : undefined}
      <MetadataForm
        className={styles.form}
        componentClass={componentClass}
        onChange={onFormChange}
        onSubmit={onFormSubmit}
        disabled={disabled}
        vertical={vertical}
        initialValuesSetter={initialValuesSetter}
        formRef={formRef}
        onInvalidSubmit={onInvalidSubmit}
        updateOnSideEffect={updateOnSideEffect}
        isSaveDisabled={isSaveDisabled}
      >
        <MetadataFields
          disabled={disabled}
          className={styles.fields}
          prefix={prefix}
          fields={metadataView.view_fields}
          values={metadataValues}
          vertical={vertical}
          fieldRenderer={fieldRenderer}
          onLayoutChanged={onLayoutChanged}
          objectType={objectType}
          onModifierValueChange={onModifierValueChange}
        />
        {children}
      </MetadataForm>
    </div>
  );
};

export default MetadataFormContainer;
